import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../contexts/ToastContext';
import './PaymentCheckout.css';

const PaymentCheckout: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState<boolean>(true);
  const [verifying, setVerifying] = useState<boolean>(false);
  const orderId = searchParams.get('orderId');
  const paymentMethod = searchParams.get('paymentMethod');
  const paymentId = searchParams.get('paymentId');
  const token = searchParams.get('token'); // PayPal token

  const verifyPayment = useCallback(async (paymentIdToVerify: string): Promise<void> => {
    setVerifying(true);
    try {
      const response = await api.post('/payment/verify', {
        orderId: parseInt(orderId || '0'),
        paymentId: paymentIdToVerify,
        paymentMethod: paymentMethod
      });

      if (response.data) {
        success('Payment confirmed! Your order is being processed.');
        setTimeout(() => {
          navigate('/history');
        }, 2000);
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      showError('Payment verification failed. Please contact support if payment was processed.');
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } finally {
      setVerifying(false);
    }
  }, [navigate, orderId, paymentMethod, showError, success]);

  const fetchOrderPaymentId = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      if (response.data) {
        // Handle both old format (order directly) and new format (order in nested object)
        const orderData = response.data.order || response.data;
        const squarePaymentId = response.data.squarePaymentId || orderData.squarePaymentId;
        
        if (squarePaymentId) {
          verifyPayment(squarePaymentId);
        } else {
          setLoading(false);
          showError('Payment ID not found. Please contact support if payment was processed.');
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch order:', error);
      setLoading(false);
      showError('Failed to verify payment. Please contact support if payment was processed.');
    }
  }, [orderId, showError, verifyPayment]);

  useEffect(() => {
    if (orderId && paymentMethod) {
      // For PayPal, token is in the URL params
      // For Square, paymentId might be in URL params or we need to get it from the order
      const paymentIdToUse = paymentId || token || searchParams.get('PayerID') || searchParams.get('paymentId') || searchParams.get('id');

      if (paymentMethod === 'Square') {
        // For Square, if no paymentId in URL, we need to fetch it from the order
        if (!paymentIdToUse) {
          fetchOrderPaymentId();
        } else {
          verifyPayment(paymentIdToUse);
        }
      } else if (paymentIdToUse) {
        verifyPayment(paymentIdToUse);
      } else {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [fetchOrderPaymentId, orderId, paymentId, paymentMethod, searchParams, token, verifyPayment]);

  if (loading || verifying) {
    return (
      <div className="payment-checkout">
        <div className="container">
          <div className="payment-status">
            <div className="spinner"></div>
            <h2>{verifying ? 'Verifying Payment...' : 'Processing...'}</h2>
            <p>Please wait while we process your payment.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-checkout">
      <div className="container">
        <div className="payment-status">
          <h2>Payment Status</h2>
          <p>Your payment is being processed.</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentCheckout;

